import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styles from './Fiscal.module.css';
import SideBarGestaoAdmin from '../components/SideBarGestaoAdmin';
import MobileMenu from '../../../shared/components/MobileMenu';
import HeaderAdmin from '../components/HeaderAdmin';
import NotasEmitidas from '../components/NotasEmitidas';
import { MdReceiptLong, MdEdit, MdCheck, MdClose, MdCheckCircle, MdWarningAmber, MdLock } from 'react-icons/md';
import usePlano from '../../../shared/hooks/usePlano';

const API_URL = process.env.REACT_APP_API_URL;

const UFS = ['AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO'];

const REGIMES = [
  { valor: 1, rotulo: 'Simples Nacional (inclui MEI)' },
  { valor: 2, rotulo: 'Simples Nacional - excesso de sublimite' },
  { valor: 3, rotulo: 'Regime Normal (Lucro Presumido/Real)' }
];

const ORIGENS = [
  { valor: 0, rotulo: '0 - Nacional' },
  { valor: 1, rotulo: '1 - Estrangeira (importação direta)' },
  { valor: 2, rotulo: '2 - Estrangeira (mercado interno)' },
  { valor: 3, rotulo: '3 - Nacional (importação 40% a 70%)' },
  { valor: 4, rotulo: '4 - Nacional (processos básicos)' },
  { valor: 5, rotulo: '5 - Nacional (importação até 40%)' },
  { valor: 6, rotulo: '6 - Estrangeira (sem similar nacional)' },
  { valor: 7, rotulo: '7 - Estrangeira (mercado interno, sem similar)' },
  { valor: 8, rotulo: '8 - Nacional (importação acima de 70%)' }
];

function cabecalhos(json = true) {
  const token = localStorage.getItem('token');
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
}

async function chamar(caminho, opcoes = {}) {
  const res = await fetch(`${API_URL}${caminho}`, opcoes);
  const dados = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(dados.error || 'Falha na requisição');
  return dados;
}

const FORM_VAZIO = {
  cnpj: '', razao_social: '', nome_fantasia: '', ie: '', regime: '',
  cep: '', logradouro: '', numero: '', complemento: '', bairro: '', municipio: '', uf: ''
};

const PADRAO_VAZIO = { id_categoria: '', fiscal_ncm: '', fiscal_cfop: '5102', fiscal_cst_csosn: '', fiscal_origem: '0', fiscal_unidade: 'UN', somente_vazios: true };

function Fiscal() {
  const estabelecimentoId = localStorage.getItem('estabelecimento_id');

  const [empresa, setEmpresa] = useState(null);
  const [form, setForm] = useState(FORM_VAZIO);
  const [ambiente, setAmbiente] = useState('homologacao');
  const [cscId, setCscId] = useState('');
  const [cscValor, setCscValor] = useState('');
  const [senhaCert, setSenhaCert] = useState('');
  const [ocupado, setOcupado] = useState('');
  const inputCertRef = useRef(null);

  const [itens, setItens] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [resumoItens, setResumoItens] = useState({ total: 0, completos: 0 });
  const [filtro, setFiltro] = useState('');
  const [soPendentes, setSoPendentes] = useState(false);
  const [idEmEdicao, setIdEmEdicao] = useState(null);
  const [edicao, setEdicao] = useState({});
  const [padrao, setPadrao] = useState(PADRAO_VAZIO);

  // Fiscal é do plano Full Speed. O menu já vem bloqueado; isto cobre quem
  // digita /admin/fiscal na mão (o servidor também responde 403).
  const { temRecurso } = usePlano();
  const [bloqueadoPeloServidor, setBloqueadoPeloServidor] = useState(false);
  const foraDoPlano = bloqueadoPeloServidor || !temRecurso('fiscal');
  const tratarFalhaDeCarga = (mensagem, erro) => {
    if (/plano Full Speed/.test(erro.message)) setBloqueadoPeloServidor(true);
    else alert(`${mensagem}: ${erro.message}`);
  };

  const regime = empresa?.regime || null;
  const rotuloCst = Number(regime) === 3 ? 'CST' : 'CSOSN';

  const carregarEmpresa = useCallback(async () => {
    if (!estabelecimentoId) return;
    try {
      const dados = await chamar(`/fiscal/empresa?id_estabelecimento=${estabelecimentoId}`, { headers: cabecalhos() });
      setEmpresa(dados);
      setAmbiente(dados.ambiente);
      setForm({
        cnpj: dados.cnpj, razao_social: dados.razao_social, nome_fantasia: dados.nome_fantasia, ie: dados.ie,
        regime: dados.regime || '', cep: dados.cep, logradouro: dados.logradouro, numero: dados.numero,
        complemento: dados.complemento, bairro: dados.bairro, municipio: dados.municipio, uf: dados.uf
      });
    } catch (error) {
      tratarFalhaDeCarga('Erro ao carregar o cadastro fiscal', error);
    }
  }, [estabelecimentoId]);

  const carregarItens = useCallback(async () => {
    if (!estabelecimentoId) return;
    try {
      const dados = await chamar(`/fiscal/itens?id_estabelecimento=${estabelecimentoId}`, { headers: cabecalhos() });
      setItens(dados.itens);
      setCategorias(dados.categorias);
      setResumoItens({ total: dados.total, completos: dados.completos });
    } catch (error) {
      tratarFalhaDeCarga('Erro ao carregar os produtos', error);
    }
  }, [estabelecimentoId]);

  useEffect(() => { carregarEmpresa(); carregarItens(); }, [carregarEmpresa, carregarItens]);

  // Mostra no formulário o ID do CSC do ambiente escolhido (o CSC em si nunca volta do servidor).
  useEffect(() => {
    setCscId(empresa?.csc?.[ambiente]?.id || '');
    setCscValor('');
  }, [empresa, ambiente]);

  const executar = async (rotulo, fn) => {
    setOcupado(rotulo);
    try { await fn(); } catch (error) { alert(error.message); } finally { setOcupado(''); }
  };

  const salvarCadastro = () => executar('cadastro', async () => {
    // Produção emite nota com valor fiscal de verdade: confirmação explícita ao trocar.
    const indoParaProducao = ambiente === 'producao' && empresa?.ambiente !== 'producao';
    if (indoParaProducao && !window.confirm(
      'ATENÇÃO: no ambiente de PRODUÇÃO as notas passam a ter valor fiscal real (imposto, SEFAZ).\n\n' +
      'A emissão automática será desligada e precisará ser ligada de novo.\n\nConfirma a mudança para Produção?'
    )) return;

    const dados = await chamar('/fiscal/empresa', {
      method: 'PUT',
      headers: cabecalhos(),
      body: JSON.stringify({
        id_estabelecimento: estabelecimentoId, ambiente, ...form,
        ...(indoParaProducao ? { confirmar_producao: true } : {}),
        regime: form.regime === '' ? null : Number(form.regime),
        csc: { [ambiente]: { id: cscId, csc: cscValor } }
      })
    });
    setEmpresa(dados);
    alert('Cadastro fiscal salvo!');
  });

  const enviarCertificado = (evento) => {
    const arquivo = evento.target.files?.[0];
    evento.target.value = '';
    if (!arquivo) return;
    if (!senhaCert) { alert('Digite a senha do certificado antes de escolher o arquivo.'); return; }
    executar('certificado', async () => {
      const formData = new FormData();
      formData.append('id_estabelecimento', estabelecimentoId);
      formData.append('senha', senhaCert);
      formData.append('arquivo', arquivo);
      const dados = await chamar('/fiscal/empresa/certificado', { method: 'POST', headers: cabecalhos(false), body: formData });
      setEmpresa(dados);
      setSenhaCert('');
      alert('Certificado salvo! Clique em "Sincronizar com o provedor" para enviá-lo.');
    });
  };

  const sincronizar = () => executar('sincronizar', async () => {
    const dados = await chamar('/fiscal/empresa/sincronizar', {
      method: 'POST', headers: cabecalhos(), body: JSON.stringify({ id_estabelecimento: estabelecimentoId })
    });
    setEmpresa(dados);
    alert('Empresa sincronizada com o provedor fiscal.');
  });

  const alternarAtivo = () => executar('ativo', async () => {
    const dados = await chamar('/fiscal/empresa', {
      method: 'PUT', headers: cabecalhos(),
      body: JSON.stringify({ id_estabelecimento: estabelecimentoId, ativo: !empresa.ativo })
    });
    setEmpresa(dados);
  });

  const alternarAutomatica = () => {
    if (!empresa.emissao_automatica && !window.confirm(
      'Ligar a emissão automática?\n\nA partir de agora, todo pedido ENTREGUE e já com a forma de pagamento definida terá a NFC-e emitida sozinha ' +
      `(ambiente: ${empresa.ambiente === 'producao' ? 'PRODUÇÃO, com valor fiscal' : 'homologação, só teste'}).\n` +
      'Pedidos feitos antes de agora e pedidos do iFood não entram.'
    )) return;
    executar('automatica', async () => {
      const dados = await chamar('/fiscal/empresa', {
        method: 'PUT', headers: cabecalhos(),
        body: JSON.stringify({ id_estabelecimento: estabelecimentoId, emissao_automatica: !empresa.emissao_automatica })
      });
      setEmpresa(dados);
    });
  };

  const iniciarEdicao = (item) => {
    setIdEmEdicao(item.id_item);
    setEdicao({
      fiscal_ncm: item.fiscal_ncm || '', fiscal_cfop: item.fiscal_cfop || '', fiscal_cst_csosn: item.fiscal_cst_csosn || '',
      fiscal_origem: item.fiscal_origem ?? '', fiscal_unidade: item.fiscal_unidade || ''
    });
  };

  const salvarEdicao = (id) => executar('item', async () => {
    await chamar(`/fiscal/itens/${id}`, {
      method: 'PUT', headers: cabecalhos(),
      body: JSON.stringify({ id_estabelecimento: estabelecimentoId, ...edicao })
    });
    setIdEmEdicao(null);
    await carregarItens();
  });

  const aplicarPadrao = () => executar('padrao', async () => {
    const alvo = padrao.id_categoria
      ? categorias.find((c) => String(c.id_categoria) === String(padrao.id_categoria))?.nome_categoria
      : 'todos os produtos';
    if (!window.confirm(`Aplicar esses dados fiscais em: ${alvo}?${padrao.somente_vazios ? '\n(Só nos campos ainda vazios.)' : '\n(Sobrescreve os campos já preenchidos.)'}`)) return;
    const dados = await chamar('/fiscal/itens/aplicar-padrao', {
      method: 'POST', headers: cabecalhos(),
      body: JSON.stringify({ id_estabelecimento: estabelecimentoId, ...padrao, id_categoria: padrao.id_categoria || null })
    });
    alert(`${dados.atualizados} de ${dados.total} produtos atualizados.`);
    await carregarItens();
  });

  const itensVisiveis = useMemo(() => {
    const termo = filtro.trim().toLowerCase();
    return itens.filter((i) =>
      (!soPendentes || !i.fiscal_completo) &&
      (!termo || i.nome_item.toLowerCase().includes(termo) || (i.categoria || '').toLowerCase().includes(termo))
    );
  }, [itens, filtro, soPendentes]);

  const campo = (rotulo, chave, extra = {}) => (
    <label className={styles.campo}>
      <span className={styles.campoRotulo}>{rotulo}</span>
      <input
        className={styles.input}
        value={form[chave]}
        onChange={(e) => setForm({ ...form, [chave]: e.target.value })}
        {...extra}
      />
    </label>
  );

  if (foraDoPlano) {
    return (
      <div className={styles.wrapperDashboard}>
        <MobileMenu />
        <SideBarGestaoAdmin />
        <div className={styles.containerDashboard}>
          <HeaderAdmin />
          <div className={styles.contentDashboard}>
            <div className={styles.cabecalhoPagina}>
              <span className={styles.iconePagina}><MdReceiptLong /></span>
              <div className={styles.cabecalhoPaginaTextos}>
                <h2 className={styles.tituloPagina}>Fiscal (NFC-e)</h2>
              </div>
            </div>
            <section className={styles.painel}>
              <strong className={styles.painelTitulo}><MdLock /> Disponível no plano Full Speed</strong>
              <p className={styles.textoMuted}>
                A emissão de NFC-e direto do pedido faz parte do plano Full Speed. Fale com o suporte do Speed Order para fazer o upgrade do seu plano.
              </p>
            </section>
          </div>
        </div>
      </div>
    );
  }

  if (!estabelecimentoId) {
    return <div className={styles.wrapperDashboard}><SideBarGestaoAdmin /><p className={styles.mensagemVazia}>Selecione um estabelecimento.</p></div>;
  }

  const pendencias = empresa?.pendencias || [];

  return (
    <div className={styles.wrapperDashboard}>
      <MobileMenu />
      <SideBarGestaoAdmin />
      <div className={styles.containerDashboard}>
        <HeaderAdmin />
        <div className={styles.contentDashboard}>
          <div className={styles.cabecalhoPagina}>
            <span className={styles.iconePagina}><MdReceiptLong /></span>
            <div className={styles.cabecalhoPaginaTextos}>
              <h2 className={styles.tituloPagina}>Fiscal (NFC-e)</h2>
              <p className={styles.subtituloPagina}>Cadastro do emitente e dados fiscais dos produtos — necessários para emitir a nota do pedido.</p>
            </div>
            {empresa && (
              <button
                className={empresa.ativo ? styles.btnDesativar : styles.btnPrimario}
                onClick={alternarAtivo}
                disabled={ocupado === 'ativo'}
              >
                {empresa.ativo ? 'Emissão ativa — desativar' : 'Ativar emissão'}
              </button>
            )}
          </div>

          {empresa && (
            <div className={pendencias.length ? styles.avisoPendente : styles.avisoOk}>
              {pendencias.length ? <MdWarningAmber /> : <MdCheckCircle />}
              <span>
                {pendencias.length
                  ? `Falta para emitir (${empresa.ambiente === 'producao' ? 'produção' : 'homologação'}): ${pendencias.join(', ')}.`
                  : `Cadastro completo para ${empresa.ambiente === 'producao' ? 'PRODUÇÃO' : 'homologação (notas de teste, sem valor fiscal)'}.`}
                {!empresa.provedor_configurado && ' O servidor não tem FOCUSNFE_REVENDA_TOKEN — a sincronização com o provedor não vai funcionar.'}
              </span>
            </div>
          )}

          {empresa?.certificado_dias_restantes !== null && empresa?.certificado_dias_restantes !== undefined && empresa.certificado_dias_restantes <= 30 && (
            <div className={styles.avisoPendente}>
              <MdWarningAmber />
              <span>
                {empresa.certificado_dias_restantes < 0
                  ? 'O certificado digital VENCEU — nenhuma nota pode ser emitida até enviar um certificado novo.'
                  : `O certificado digital vence em ${empresa.certificado_dias_restantes} dia(s) (${new Date(empresa.certificado_validade).toLocaleDateString('pt-BR')}). Providencie um novo e envie aqui antes disso.`}
              </span>
            </div>
          )}

          {empresa?.ativo && (
            <section className={styles.painel}>
              <strong className={styles.painelTitulo}>Emissão automática</strong>
              <p className={styles.textoMuted}>
                {empresa.emissao_automatica
                  ? `Ligada desde ${new Date(empresa.emissao_automatica_desde).toLocaleString('pt-BR')}: pedidos entregues e com pagamento definido têm a NFC-e emitida sozinha, sem ninguém clicar. Se algum não puder ser emitido (produto sem dado fiscal, por exemplo), ele aparece em "Notas emitidas" com o motivo.`
                  : 'Desligada: a nota é emitida pelo botão "Emitir NFC-e" dentro de cada pedido. Ligada, o sistema emite sozinho os pedidos entregues e pagos, feitos a partir do momento em que você ligar. Pedidos do iFood não entram.'}
              </p>
              <div className={styles.acoes}>
                <button className={empresa.emissao_automatica ? styles.btnDesativar : styles.btnPrimario} onClick={alternarAutomatica} disabled={ocupado === 'automatica'}>
                  {empresa.emissao_automatica ? 'Ligada — desligar' : 'Ligar emissão automática'}
                </button>
              </div>
            </section>
          )}

          <section className={styles.painel}>
            <strong className={styles.painelTitulo}>Dados do emitente</strong>
            <div className={styles.grade}>
              {campo('CNPJ', 'cnpj', { placeholder: 'Somente números', inputMode: 'numeric' })}
              {campo('Razão social', 'razao_social')}
              {campo('Nome fantasia', 'nome_fantasia')}
              {campo('Inscrição estadual', 'ie', { placeholder: 'Ou ISENTO' })}
              <label className={styles.campo}>
                <span className={styles.campoRotulo}>Regime tributário</span>
                <select className={styles.input} value={form.regime} onChange={(e) => setForm({ ...form, regime: e.target.value })}>
                  <option value="">Selecione</option>
                  {REGIMES.map((r) => <option key={r.valor} value={r.valor}>{r.rotulo}</option>)}
                </select>
              </label>
              {campo('CEP', 'cep', { placeholder: 'Somente números', inputMode: 'numeric' })}
              {campo('Logradouro', 'logradouro')}
              {campo('Número', 'numero')}
              {campo('Complemento', 'complemento')}
              {campo('Bairro', 'bairro')}
              {campo('Município', 'municipio')}
              <label className={styles.campo}>
                <span className={styles.campoRotulo}>UF</span>
                <select className={styles.input} value={form.uf} onChange={(e) => setForm({ ...form, uf: e.target.value })}>
                  <option value="">UF</option>
                  {UFS.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
                </select>
              </label>
            </div>
          </section>

          <section className={styles.painel}>
            <strong className={styles.painelTitulo}>Ambiente, CSC e certificado digital</strong>
            <div className={styles.grade}>
              <label className={styles.campo}>
                <span className={styles.campoRotulo}>Ambiente de emissão</span>
                <select className={styles.input} value={ambiente} onChange={(e) => setAmbiente(e.target.value)}>
                  <option value="homologacao">Homologação (teste, sem valor fiscal)</option>
                  <option value="producao">Produção (valor fiscal real)</option>
                </select>
              </label>
              <label className={styles.campo}>
                <span className={styles.campoRotulo}>ID do CSC ({ambiente})</span>
                <input className={styles.input} value={cscId} onChange={(e) => setCscId(e.target.value)} inputMode="numeric" placeholder="Ex: 1" />
              </label>
              <label className={styles.campo}>
                <span className={styles.campoRotulo}>CSC ({ambiente}) {empresa?.csc?.[ambiente]?.configurado && '· já cadastrado'}</span>
                <input
                  className={styles.input}
                  type="password"
                  autoComplete="new-password"
                  value={cscValor}
                  onChange={(e) => setCscValor(e.target.value)}
                  placeholder={empresa?.csc?.[ambiente]?.configurado ? 'Deixe em branco para manter' : 'Código de segurança do contribuinte'}
                />
              </label>
            </div>

            <div className={styles.linhaCertificado}>
              <label className={styles.campo}>
                <span className={styles.campoRotulo}>
                  Certificado A1 {empresa?.certificado_enviado ? '· enviado' : '· não enviado'}
                  {empresa?.certificado_validade && ` · vence em ${new Date(empresa.certificado_validade).toLocaleDateString('pt-BR')}`}
                </span>
                <input
                  className={styles.input}
                  type="password"
                  autoComplete="new-password"
                  value={senhaCert}
                  onChange={(e) => setSenhaCert(e.target.value)}
                  placeholder="Senha do certificado"
                />
              </label>
              <button className={styles.btnSecundario} onClick={() => inputCertRef.current?.click()} disabled={ocupado === 'certificado'}>
                {ocupado === 'certificado' ? 'Enviando...' : empresa?.certificado_enviado ? 'Trocar arquivo .pfx' : 'Enviar arquivo .pfx'}
              </button>
              <input ref={inputCertRef} type="file" accept=".pfx,.p12" hidden onChange={enviarCertificado} />
            </div>

            <div className={styles.acoes}>
              <button className={styles.btnPrimario} onClick={salvarCadastro} disabled={ocupado === 'cadastro'}>
                {ocupado === 'cadastro' ? 'Salvando...' : 'Salvar cadastro'}
              </button>
              <button className={styles.btnSecundario} onClick={sincronizar} disabled={ocupado === 'sincronizar' || pendencias.length > 0}>
                {ocupado === 'sincronizar' ? 'Sincronizando...' : 'Sincronizar com o provedor'}
              </button>
              {empresa?.sincronizado_em && (
                <span className={styles.textoMuted}>Última sincronização: {new Date(empresa.sincronizado_em).toLocaleString('pt-BR')}</span>
              )}
            </div>
          </section>

          <section className={styles.painel}>
            <strong className={styles.painelTitulo}>Dados fiscais dos produtos</strong>
            <p className={styles.textoMuted}>
              {resumoItens.completos} de {resumoItens.total} produtos prontos para nota.
              Produto sem NCM, CFOP, {rotuloCst}, origem e unidade não entra na NFC-e. Peça o NCM e o {rotuloCst} certos ao seu contador.
            </p>

            <div className={styles.gradePadrao}>
              <label className={styles.campo}>
                <span className={styles.campoRotulo}>Categoria</span>
                <select className={styles.input} value={padrao.id_categoria} onChange={(e) => setPadrao({ ...padrao, id_categoria: e.target.value })}>
                  <option value="">Todos os produtos</option>
                  {categorias.map((c) => <option key={c.id_categoria} value={c.id_categoria}>{c.nome_categoria}</option>)}
                </select>
              </label>
              <label className={styles.campo}>
                <span className={styles.campoRotulo}>NCM</span>
                <input className={styles.input} value={padrao.fiscal_ncm} maxLength={8} placeholder="8 dígitos" inputMode="numeric" onChange={(e) => setPadrao({ ...padrao, fiscal_ncm: e.target.value })} />
              </label>
              <label className={styles.campo}>
                <span className={styles.campoRotulo}>CFOP</span>
                <input className={styles.input} value={padrao.fiscal_cfop} maxLength={4} inputMode="numeric" onChange={(e) => setPadrao({ ...padrao, fiscal_cfop: e.target.value })} />
              </label>
              <label className={styles.campo}>
                <span className={styles.campoRotulo}>{rotuloCst}</span>
                <input className={styles.input} value={padrao.fiscal_cst_csosn} maxLength={3} placeholder={Number(regime) === 3 ? '00' : '102'} inputMode="numeric" onChange={(e) => setPadrao({ ...padrao, fiscal_cst_csosn: e.target.value })} />
              </label>
              <label className={styles.campo}>
                <span className={styles.campoRotulo}>Origem</span>
                <select className={styles.input} value={padrao.fiscal_origem} onChange={(e) => setPadrao({ ...padrao, fiscal_origem: e.target.value })}>
                  {ORIGENS.map((o) => <option key={o.valor} value={o.valor}>{o.rotulo}</option>)}
                </select>
              </label>
              <label className={styles.campo}>
                <span className={styles.campoRotulo}>Unidade</span>
                <input className={styles.input} value={padrao.fiscal_unidade} maxLength={6} onChange={(e) => setPadrao({ ...padrao, fiscal_unidade: e.target.value })} />
              </label>
              <button className={styles.btnPrimario} onClick={aplicarPadrao} disabled={ocupado === 'padrao'}>
                {ocupado === 'padrao' ? 'Aplicando...' : 'Aplicar'}
              </button>
            </div>
            <label className={styles.check}>
              <input type="checkbox" checked={padrao.somente_vazios} onChange={(e) => setPadrao({ ...padrao, somente_vazios: e.target.checked })} />
              Preencher só os campos ainda vazios (não sobrescrever o que já está cadastrado)
            </label>

            <div className={styles.filtros}>
              <input className={styles.input} placeholder="Buscar produto ou categoria" value={filtro} onChange={(e) => setFiltro(e.target.value)} />
              <label className={styles.check}>
                <input type="checkbox" checked={soPendentes} onChange={(e) => setSoPendentes(e.target.checked)} />
                Só pendentes
              </label>
            </div>

            <div className={styles.tabelaWrapper}>
              {itensVisiveis.length === 0 ? (
                <p className={styles.mensagemVazia}>Nenhum produto encontrado.</p>
              ) : (
                <table className={styles.tabela}>
                  <thead>
                    <tr>
                      <th>Produto</th><th>Categoria</th><th>NCM</th><th>CFOP</th><th>{rotuloCst}</th><th>Origem</th><th>Un.</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {itensVisiveis.map((item) => (
                      <tr key={item.id_item}>
                        <td data-label="Produto">
                          {item.fiscal_completo ? <MdCheckCircle className={styles.iconeOk} title="Pronto" /> : <MdWarningAmber className={styles.iconeAviso} title="Faltam dados" />}{' '}
                          {item.nome_item}
                        </td>
                        <td data-label="Categoria" className={styles.colunaMuted}>{item.categoria || '—'}</td>
                        {idEmEdicao === item.id_item ? (
                          <>
                            <td data-label="NCM"><input className={styles.inputLinha} value={edicao.fiscal_ncm} maxLength={8} onChange={(e) => setEdicao({ ...edicao, fiscal_ncm: e.target.value })} /></td>
                            <td data-label="CFOP"><input className={styles.inputLinhaCurto} value={edicao.fiscal_cfop} maxLength={4} onChange={(e) => setEdicao({ ...edicao, fiscal_cfop: e.target.value })} /></td>
                            <td data-label={rotuloCst}><input className={styles.inputLinhaCurto} value={edicao.fiscal_cst_csosn} maxLength={3} onChange={(e) => setEdicao({ ...edicao, fiscal_cst_csosn: e.target.value })} /></td>
                            <td data-label="Origem">
                              <select className={styles.inputLinhaCurto} value={edicao.fiscal_origem} onChange={(e) => setEdicao({ ...edicao, fiscal_origem: e.target.value })}>
                                <option value="">—</option>
                                {ORIGENS.map((o) => <option key={o.valor} value={o.valor}>{o.valor}</option>)}
                              </select>
                            </td>
                            <td data-label="Un."><input className={styles.inputLinhaCurto} value={edicao.fiscal_unidade} maxLength={6} onChange={(e) => setEdicao({ ...edicao, fiscal_unidade: e.target.value })} /></td>
                            <td className={styles.colunaAcoes}>
                              <button className={styles.btnIcone} onClick={() => salvarEdicao(item.id_item)} title="Salvar"><MdCheck /></button>
                              <button className={styles.btnIcone} onClick={() => setIdEmEdicao(null)} title="Cancelar"><MdClose /></button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td data-label="NCM">{item.fiscal_ncm || '—'}</td>
                            <td data-label="CFOP">{item.fiscal_cfop || '—'}</td>
                            <td data-label={rotuloCst}>{item.fiscal_cst_csosn || '—'}</td>
                            <td data-label="Origem">{item.fiscal_origem ?? '—'}</td>
                            <td data-label="Un.">{item.fiscal_unidade || '—'}</td>
                            <td className={styles.colunaAcoes}>
                              <button className={styles.btnIcone} onClick={() => iniciarEdicao(item)} title="Editar"><MdEdit /></button>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          <NotasEmitidas estabelecimentoId={estabelecimentoId} />
        </div>
      </div>
    </div>
  );
}

export default Fiscal;
